import React, { useState } from 'react'
import { GetBodyContents, IsNotEmpty, IsValidReactObj } from '../Helpers'
import HtmlReactParser from 'html-react-parser';

import { Dialog, DialogTitle, Button, DialogActions, DialogContent, Typography, useTheme } from '@mui/material';
import { SnackBarDisplayProps, useSnackBarDisplay } from './SnackBarDisplay';
import ConditionalDisplay from './ConditionalDisplay';
import { useContextualAiLayers } from './ai/ContextualAiLayerContext';

export interface ExError extends Error {
    componentName: string;
    jsonMethodParams: any;
    userFriendlyMessage: string;
}

export interface ErrorDisplayState {
    show: boolean;
    message: string | React.ReactNode;
    subtitle: string;
    displayAsValidation: boolean;
}

interface DetailErrorDisplayProps extends ErrorDisplayProps {
    error: ErrorDisplayState;
    setError: React.Dispatch<React.SetStateAction<ErrorDisplayState>>;
    snackbarDisplay: (props: SnackBarDisplayProps) => JSX.Element;
}

export interface ErrorDisplayProps {
    displayType?: 'dialog' | 'message';
}

const ErrorDisplay = ({ displayType = 'dialog', error, setError, snackbarDisplay }: DetailErrorDisplayProps) => {
    const theme = useTheme();
    const contextualAiLayers = useContextualAiLayers();
    const transientRegistered = React.useRef(false);
    React.useEffect(() => {
        if (!contextualAiLayers || displayType !== 'dialog')
            return;
        if (error.show && !transientRegistered.current) {
            contextualAiLayers.openTransient();
            transientRegistered.current = true;
        } else if (!error.show && transientRegistered.current) {
            contextualAiLayers.closeTransient();
            transientRegistered.current = false;
        }
        return () => {
            if (transientRegistered.current) {
                contextualAiLayers.closeTransient();
                transientRegistered.current = false;
            }
        };
    }, [contextualAiLayers, displayType, error.show]);

    if (displayType === 'message') {
        return (
            <ConditionalDisplay ifTrue={error.show}>
                {() => snackbarDisplay({})}
            </ConditionalDisplay>
        );
    } else {
        return (
            <Dialog className={contextualAiLayers ? 'contextual-ai-transient' : undefined} sx={contextualAiLayers ? { zIndex: contextualAiLayers.transientZIndex } : undefined} onClose={() => setError({ ...error, show: false })} aria-labelledby="customized-dialog-title" open={error.show} fullWidth={true} color="red">
                <DialogTitle id="customized-dialog-title" sx={{ margin: 0, padding: theme.spacing(2) }}>
                    <Typography>{error?.subtitle ?? 'Error'}</Typography>
                </DialogTitle>
                <DialogContent dividers sx={{ padding: theme.spacing(2) }}>
                    {IsValidReactObj(error?.message) ? error?.message : (typeof error?.message === 'string' ? error?.message : "Unknown Error")}
                </DialogContent>
                <DialogActions sx={{ padding: theme.spacing(1) }}>
                    <Button variant='contained' onClick={() => setError({ ...error, show: false })}>Ok</Button>
                </DialogActions>
            </Dialog>
        );
    }
};


export function useErrorDisplay(): [(props: ErrorDisplayProps) => JSX.Element, (message: string | React.ReactNode, subtitle: string, displayAsValidation?: boolean) => void, () => void] {
    const [error, setErrorFunc] = useState<ErrorDisplayState>({ message: null, subtitle: null, show: false, displayAsValidation: false });
    const [SnackBarDisplay, setMessage] = useSnackBarDisplay();
    let setError: (show: boolean, message: string | React.ReactNode, subtitle: string, displayAsValidation: boolean) => void = async (show, message, subtitle, displayAsValidation) => {

        if (!displayAsValidation) {
            var errObj = message as Error;

            if (IsNotEmpty(errObj?.message)) {
                // console.log("__ Synergist.UI -- Proccessing Error Object");

                var exErrObj = message as ExError;
                message = IsNotEmpty(exErrObj?.userFriendlyMessage)
                    ? exErrObj.userFriendlyMessage
                    : errObj.message;

                // try {
                //     await new SystemClient().LogError(exErrObj?.componentName, errObj, exErrObj?.jsonMethodParams);
                // } catch (error) {
                //     // swallow error, this is likely a connectivity related error.
                //     // we are in a display fucntion, we don't want to throw an error here.
                // }
            }

            // These are api errors which will already have been logged.
            if (typeof message === 'string' && message.startsWith("<")) {
                // console.log("__ Synergist.UI -- Parsing HTML");
                message = HtmlReactParser(`<div>${GetBodyContents(message)}</div>`);
            }
            else if (typeof message === 'string' && message.trim().startsWith("{")) {
                console.log("__ Synergist.UI -- Parsing Json");
                let obj = JSON.parse(message);
                message = <div>
                    <p>{obj.Message}</p>
                    <p>RequestId: {obj.RequestId}</p>
                </div>
            }
            else {
                console.log('__ Synergist.UI -- unknown error data type');
                console.log(message);
            }

            if (show) {
                console.error(message);
            }
        }else{
                message = HtmlReactParser(`${message}`);
        }

        setMessage(`${subtitle}: ${message ?? ''}`, true);
        setErrorFunc({ show: show, message: message, subtitle: subtitle, displayAsValidation: displayAsValidation });
    };

    return [({ displayType }: ErrorDisplayProps) => ErrorDisplay({ displayType: displayType, error: error, setError: setErrorFunc, snackbarDisplay: SnackBarDisplay }), (message: string | React.ReactNode, subtitle: string, displayAsValidation: boolean = false) => { setError(true, message, subtitle, displayAsValidation) }, () => setError(false, null, null, false)];
};
